// src/PublicUser/pages/blogspage.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import BlogNavbar from '../components/common/Navbar';
import BlogFooter from '../components/common/Footer';
import BlogHero from '../components/blogs/BlogHero';
import BlogCard from '../components/blogs/BlogCard';
import CategoryFilter from '../components/blogs/CategoryFilter';
import BlogPagination from '../components/blogs/BlogPagination';
import { BlogService } from '../../services/blogService';

import '../css/BlogPage.css';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const postsPerPage = 6;
  const searchTimeoutRef = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await BlogService.getAllBlogCategory();
        const categoriesData = response.data || response;
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    
    fetchCategories();
  }, []);

  // Fetch blogs based on selected category or search
  const fetchBlogs = useCallback(async () => {
    // Don't fetch if we're searching (search has its own handler)
    if (isSearching) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (selectedCategory !== 'All' && selectedCategoryId) {
        response = await BlogService.getBlogsByCategory(selectedCategoryId);
      } else {
        response = await BlogService.getBlogsSummery();
      }
      
      const blogsData = response.data || response;
      setBlogs(blogsData);
    } catch (err) {
      setError(err.message || 'Failed to fetch blogs');
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedCategoryId, isSearching]);

  // Handle search when debounced term changes
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setIsSearching(false);
      fetchBlogs();
      return;
    }
    
    setIsSearching(true);
    setLoading(true);
    setError(null);
    
    try {
      const response = await BlogService.searchBlogs(query);
      const searchResults = response.data || response;
      setBlogs(searchResults);
    } catch (err) {
      setError(err.message || 'Failed to search blogs');
      console.error('Error searching blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchBlogs]);

  // Trigger search when debounced term changes
  useEffect(() => {
    performSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, performSearch]);

  // Fetch blogs when category changes (and not searching)
  useEffect(() => {
    if (!isSearching) {
      fetchBlogs();
    }
  }, [fetchBlogs, isSearching]);

  // Reset search when category changes
  const handleCategoryChange = (categoryName, categoryId) => {
    setSelectedCategory(categoryName);
    setSelectedCategoryId(categoryId);
    setSearchTerm(''); // Clear search box
    setDebouncedSearchTerm(''); // Clear debounced search
    setIsSearching(false);
    setCurrentPage(1);
  };

  // Handle search input change
  const handleSearchChange = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    
    // Reset category when searching
    if (term.trim()) {
      setSelectedCategory('All');
      setSelectedCategoryId(null);
    }
  };

  // --- Filtering Logic (for local filtering when needed) ---
  const filteredBlogs = useMemo(() => {
    // If we're searching via API, we don't need additional client filtering
    if (isSearching && debouncedSearchTerm.trim()) {
      return blogs;
    }
    
    // For category view, apply client-side search filter
    let result = blogs;
    
    if (searchTerm.trim() && !isSearching) {
      const term = searchTerm.toLowerCase();
      result = result.filter(blog => 
        blog.title?.toLowerCase().includes(term) ||
        blog.description?.toLowerCase().includes(term) ||
        blog.category?.toLowerCase().includes(term)
      );
    }
    
    return result;
  }, [blogs, searchTerm, isSearching, debouncedSearchTerm]);

  // --- Pagination Logic ---
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredBlogs.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredBlogs.length / postsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  if (loading && blogs.length === 0) return <div className="ts-blog-loader">Loading awesome content...</div>;
  if (error) return <div className="ts-blog-error">Error: {error}</div>;

  return (
    <>
        <BlogHero 
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
        />
        
        <div className="ts-blog-container">
          {/* Category Filter Row */}
          <CategoryFilter 
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            categories={categories}
          />
          
          {/* Results Count */}
          <div className="ts-blog-results-count">
            {isSearching && debouncedSearchTerm ? (
              <>Search results for "{debouncedSearchTerm}": {filteredBlogs.length} articles found</>
            ) : (
              <>Showing {currentPosts.length} of {filteredBlogs.length} articles</>
            )}
          </div>

          {/* Loading indicator for search */}
          {loading && isSearching && (
            <div className="ts-blog-loading-more">Searching...</div>
          )}

          {/* Blog Grid */}
          {currentPosts.length > 0 ? (
            <div className="ts-blog-grid">
              {currentPosts.map((blog, index) => (
                <BlogCard 
                  key={blog.id} 
                  blog={{
                    ...blog,
                    category: blog.category || blog.Category
                  }} 
                  index={index} 
                />
              ))}
            </div>
          ) : (
            <div className="ts-blog-no-results">
              <h3>No posts found</h3>
              <p>Try adjusting your search or filter to find what you're looking for.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && !isSearching && (
            <BlogPagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
    </>
  );
};

export default BlogPage;